import { default as useReactWindowScroll } from '@react-hook/window-scroll';
import debounce from 'lodash.debounce';
import React, { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import {
  directAccessUrl,
  driverLicenseUrl,
  homeUrl,
  offerDetailUrl,
  offerRepublishPaymentUrl,
} from '@urls';
import { RefHook } from '@utils/types';

export const useScrollTopOnError = (errors: Error[]) => {
  useEffect(() => {
    if (errors.length) {
      window.scrollTo({ behavior: 'smooth', top: 0 });
    }
  }, [errors]);
};

export const useToggle = (initial = false): [boolean, () => void] => {
  const [toggle, setToggle] = useState(initial);
  const toggler = () => setToggle((p) => !p);
  return [toggle, toggler];
};

export const useCounter = (): [number, () => void] => {
  const [count, setCount] = useState(0);
  const increment = () => setCount((c) => ++c);
  return [count, increment];
};

export function useIsMounted() {
  const isMounted = React.useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted; // returning "isMounted.current" wouldn't work because we would return immutable primitive
}

export const useWindowScroll = (fps = 60) => {
  const scrollY = useReactWindowScroll(fps);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    let timeout: any = null;
    const handleScroll = debounce(
      () => {
        clearTimeout(timeout);
        !isScrolling && setIsScrolling(true);
        timeout = setTimeout(() => setIsScrolling(false), 250);
      },
      50,
      { leading: true, trailing: true },
    );

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout);
    };
  }, []);

  return { scrollY, isScrolling };
};

export const useScrollEnd = (
  ref: RefHook<HTMLElement>,
  onEndReachCallback: () => void,
  options?: Partial<{
    threshold?: number;
    scrollableContentRef?: RefHook<HTMLElement>;
  }>,
): void => {
  const { threshold, scrollableContentRef } = { threshold: 250, ...options };

  useEffect(() => {
    const el = ref.current;
    const contentEl = scrollableContentRef?.current;

    if (!el || (scrollableContentRef && !contentEl)) {
      return;
    }

    const scrollable = contentEl ? el : window;

    const handleScroll = debounce(
      () => {
        const triggerCallback = contentEl
          ? el.scrollTop + window.innerHeight + threshold / 2 >= contentEl.offsetHeight
          : window.scrollY + window.innerHeight + threshold >= el.offsetHeight + el.offsetTop;

        /**
         * Component might be unmounted when it's time to trigger the
         * callback.
         */
        if (ref.current && triggerCallback) {
          onEndReachCallback();
        }
      },
      100,
      { trailing: true },
    );

    handleScroll();

    scrollable.addEventListener('scroll', handleScroll, { passive: true });

    return () => scrollable.removeEventListener('scroll', handleScroll);
  }, [ref, onEndReachCallback]);
};

export const FULL_WIDTH_PATHS = [homeUrl, driverLicenseUrl];
export const useFullWidthPath = () => {
  const { pathname, state } = useLocation<{ fromOfferList?: boolean }>();
  return useMemo(() => {
    if (FULL_WIDTH_PATHS.some((path) => matchPath(pathname, { path, exact: true }))) {
      return true;
    }
    if (
      matchPath(pathname, { path: offerDetailUrl, exact: true }) &&
      state?.fromOfferList === true
    ) {
      return true;
    }
    /** Płatność przy wznowieniu — jak kreator: pełna szerokość header + main (białe tło do krawędzi). */
    if (matchPath(pathname, { path: offerRepublishPaymentUrl, exact: true })) {
      return true;
    }
    return false;
  }, [pathname, state]);
};

export const HIDE_LOCAL_URLS = [directAccessUrl];
export const useHideLocalUrls = () => {
  const { pathname } = useLocation();
  return useMemo(
    () => !!HIDE_LOCAL_URLS.find((path) => matchPath(pathname, { path, exact: true })),
    [pathname],
  );
};
